import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _values from "lodash/values";
import _mapValues from "lodash/mapValues";
import _set from "lodash/set";
import _reduce from "lodash/reduce";
var _excluded = ["animateMS", "onOpen", "className", "overlayClassName", "closeTimeoutMS", "aria", "contentLabel", "onAfterOpen"];
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.object.keys.js";
import { useMemo } from 'react';
import { printPropDeprecationWarning, useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
/**
 * this is a local wrapper around `useDeprecatedPropsWarning`
 * with a less verbose interface.
 * all of these deprecations were made in the same version (8.10.0)
 */

var useDeprecatedProps = function useDeprecatedProps(_ref) {
  var props = _ref.props,
      warning = _ref.warning,
      componentName = _ref.componentName,
      version = _ref.version;
  useDeprecatedPropsWarning({
    component: componentName,
    props: props,
    deprecatedProps: Object.keys(props).map(function (propName) {
      return {
        deprecated: propName,
        migration: warning,
        version: version
      };
    })
  });
};
/**
 * legacy 'react-modal' npm package had an interface where:
 * ```jsx
 *  aria={{ labelledby: 'my-id', ... }}
 * ```
 * would be coverted to aria-<key> html attributes:
 * ```
 *  aria-labelledby="my-id" aria-...="..."
 * ```
 * - legacy interface: https://reactcommunity.org/react-modal/accessibility/#aria
 *
 * This function converts legacy aria prop interface
 * into spreadable object of aria html attributes
 */


var ariaAttributesFromLegacyInterface = function ariaAttributesFromLegacyInterface(_ref2) {
  var aria = _ref2.aria,
      contentLabel = _ref2.contentLabel;
  return _reduce(aria, function (memo, val, key) {
    _set(memo, "aria-".concat(key), val);

    return memo;
  }, {
    // contentLabel was the interface for aria-label in legacy third-party react-modal dependency
    'aria-label': contentLabel
  });
};
/**
 * legacy 'react-modal' npm package had an interface where
 * all various className props (className, overlayClassName, portalClassName, etc...)
 * were objects instead of strings:
 * ```jsx
 *  className={{
 *    base: 'my-classname-always-applied',
 *    afterOpen: 'my-classname-only-applied-when-visible',
 *    beforeClose: 'my-classname-only-applied-when-closing',
 *  }}
 * ```
 * - legacy-interface: https://reactcommunity.org/react-modal/
 *
 * This function warns if the deprecated interface is in use
 * and picks only the 'base' class from the className object
 * It returns a single string.
 */


var useDeprecatedClassNameProps = function useDeprecatedClassNameProps(_ref3) {
  var props = _ref3.props,
      warning = _ref3.warning,
      componentName = _ref3.componentName,
      version = _ref3.version;
  return useMemo(function () {
    return _mapValues(props, function (prop, propName) {
      if (!prop) return prop;
      if (typeof prop === 'string') return prop; // warn about deprecation

      if (process.env.NODE_ENV !== 'production') {
        var deprecatedProp = {
          version: version,
          deprecated: propName,
          migration: warning
        };
        printPropDeprecationWarning(componentName, [deprecatedProp], props);
      } // only take the base property


      return prop.base;
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [].concat(_toConsumableArray(_values(props)), [warning, componentName, version]));
};
/**
 * v8.10.0 of Modal/BaseModal changed the underlying dependency in use
 * It used to be the npm package 'react-modal', it change to npm package 'reakit'
 *
 * The legacy interface was a wide-open passthrough into the `react-modal` dependency's interface
 * This made it very complex, hard to reason about, and very difficult to maintain.
 *
 * This function normalizes the deprecated legacy 'react-modal' passthrough props with the current interface.
 * - it takes *all* props given to <Modal /> or <BaseModal />
 * - it warns when deprecated `react-modal` passthrough props are used
 * - it picks current interface props over deprecated props when both are provided
 * - it returns an object consisting of props for the current interface (ModalImpl)
 */


export var useDeprecatedReactModalProps = function useDeprecatedReactModalProps(_ref4, componentName) {
  var animateMS = _ref4.animateMS,
      onOpen = _ref4.onOpen,
      className = _ref4.className,
      overlayClassName = _ref4.overlayClassName,
      closeTimeoutMS = _ref4.closeTimeoutMS,
      aria = _ref4.aria,
      contentLabel = _ref4.contentLabel,
      onAfterOpen = _ref4.onAfterOpen,
      props = _objectWithoutProperties(_ref4, _excluded);

  /* eslint-disable react-hooks/rules-of-hooks */
  if (process.env.NODE_ENV !== 'production') {
    // animateMS more clearly defines runtime behavior
    useDeprecatedProps({
      props: {
        closeTimeoutMS: closeTimeoutMS
      },
      warning: "Use 'animateMS' prop instead.",
      componentName: componentName,
      version: '8.10.0'
    }); // deprecated 'aria' prop is converted to individual 'aria-<attribute>' props

    useDeprecatedProps({
      props: {
        aria: aria
      },
      warning: "Instead use `aria-<attribute>` props directly, e.g. 'aria-label', 'aria-labelledby', 'aria-describedby', etc...",
      componentName: componentName,
      version: '8.10.0'
    });
    useDeprecatedProps({
      props: {
        contentLabel: contentLabel
      },
      warning: "Use 'aria-label' prop instead.",
      componentName: componentName,
      version: '8.10.0'
    }); // specific event handler names have been simplified to more clearyle describe runtime behavior

    useDeprecatedProps({
      props: {
        onAfterOpen: onAfterOpen
      },
      warning: "Please use 'onOpen' instead. Only 'onOpen' will be called if both 'onOpen' and 'onAfterOpen' are defined.",
      componentName: componentName,
      version: '8.10.0'
    });
  }
  /* eslint-enable react-hooks/rules-of-hooks */


  return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, useDeprecatedClassNameProps({
    props: {
      className: className
    },
    warning: "Passing 'className' as an object with shape { base, afterOpen, beforeClose } has been deprecated. Only the 'className.base' be used. Please supply a single string instead of an object.",
    componentName: componentName,
    version: '8.10.0'
  })), useDeprecatedClassNameProps({
    props: {
      overlayClassName: overlayClassName
    },
    warning: "Passing 'overlayClassName' as an object with shape { base, afterOpen, beforeClose } has been deprecated. Only the 'className.base' be used. Please supply a single string instead of an object.",
    componentName: componentName,
    version: '8.10.0'
  })), {}, {
    // deprecated 'closeTimeoutMS' is fallback for 'animateMS' is
    animateMS: animateMS || closeTimeoutMS,
    // deprecated 'onAfterOpen' is fallback for 'onOpen'
    onOpen: onOpen || onAfterOpen
  }, ariaAttributesFromLegacyInterface({
    aria: aria,
    contentLabel: contentLabel
  })), props);
};